import { type ListAgentSessionArtifactRowsResponse, type SessionArtifact } from '@air/server-sdk';

import {
  type AssetListPill,
  type BoardListPill,
  type ChangePreviewPill,
  type TablePill,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import {
  parseWorkspaceAgentTableArtifactSummary,
  parseWorkspaceAgentTableRowsPage,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/workspaceAgentTableArtifact';

type ArtifactRowsHydration = {
  artifact: SessionArtifact | undefined;
  rowsPage: ListAgentSessionArtifactRowsResponse;
};

const orderedPayloads = <Payload>(rows: readonly { payload: Payload; position: number }[]): Payload[] =>
  [...rows].sort((left, right) => left.position - right.position).map((row) => row.payload);

export const toWorkspaceAgentAssetListPillFromArtifactRows = ({
  artifact,
  pill,
  rowsPage,
}: ArtifactRowsHydration & { pill: AssetListPill }): AssetListPill | undefined => {
  if ((artifact !== undefined && artifact.type !== 'assetList') || rowsPage.type !== 'assetList') {
    return undefined;
  }

  const items = orderedPayloads(rowsPage.data);

  if (items.length === 0) {
    return undefined;
  }

  return {
    count: Math.max(pill.count, rowsPage.pagination.total, items.length),
    id: pill.id,
    items,
    type: 'assetList',
  };
};

export const toWorkspaceAgentBoardListPillFromArtifactRows = ({
  artifact,
  pill,
  rowsPage,
}: ArtifactRowsHydration & { pill: BoardListPill }): BoardListPill | undefined => {
  if ((artifact !== undefined && artifact.type !== 'boardList') || rowsPage.type !== 'boardList') {
    return undefined;
  }

  const items = orderedPayloads(rowsPage.data);

  if (items.length === 0) {
    return undefined;
  }

  return {
    count: Math.max(pill.count, rowsPage.pagination.total, items.length),
    id: pill.id,
    items,
    type: 'boardList',
  };
};

export const toWorkspaceAgentChangePreviewPillFromArtifact = ({
  artifact,
  pill,
  rowsPage,
}: ArtifactRowsHydration & { pill: ChangePreviewPill }): ChangePreviewPill | undefined => {
  if ((artifact !== undefined && artifact.type !== 'changePreview') || rowsPage.type !== 'changePreview') {
    return undefined;
  }

  const rows = orderedPayloads(rowsPage.data);

  if (rows.length === 0) {
    return undefined;
  }

  const warnings = artifact?.type === 'changePreview' ? artifact.summary.warnings : pill.warnings;
  const title = artifact?.type === 'changePreview' ? artifact.summary.title : pill.title;

  return {
    count: Math.max(pill.count, rowsPage.pagination.total, rows.length),
    id: pill.id,
    rows,
    title,
    truncated: rowsPage.pagination.hasMore || pill.truncated,
    type: 'changePreview',
    ...(warnings && warnings.length > 0 ? { warnings } : {}),
  };
};

/** Columns live on the artifact summary, so a table cannot render from rows alone. */
export const toWorkspaceAgentTablePillFromArtifact = ({
  artifact,
  pill,
  rowsPage,
}: {
  artifact: unknown;
  pill: TablePill;
  rowsPage: unknown;
}): TablePill | undefined => {
  const summary = parseWorkspaceAgentTableArtifactSummary(artifact);
  const page = parseWorkspaceAgentTableRowsPage(rowsPage);

  if (!summary || !page || summary.columns.length === 0) {
    return undefined;
  }

  const rows = orderedPayloads(page.data);

  if (rows.length === 0) {
    return undefined;
  }

  return {
    columns: summary.columns,
    count: Math.max(summary.count ?? 0, rows.length),
    id: pill.id,
    rows,
    title: summary.title ?? pill.title,
    truncated: page.pagination.hasMore || (summary.truncated ?? false),
    type: 'table',
  };
};
